import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Trash2, Edit2, DollarSign, Calendar, MapPin } from 'lucide-react';
import { independentItinerariesService, independentClientsService } from '../../services/independent';

const IndependentItinerariesListEnhanced = () => {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('date_desc');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itinerariesData, clientsData] = await Promise.all([
        independentItinerariesService.getAll(),
        independentClientsService.getAll()
      ]);
      setItineraries(itinerariesData);
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this itinerary?')) return;
    try {
      await independentItinerariesService.delete(id);
      setItineraries(itineraries.filter(i => i.id !== id));
    } catch (error) {
      console.error('Error deleting itinerary:', error);
      alert('Failed to delete itinerary');
    }
  };

  const openPaymentModal = (itinerary) => {
    setSelectedItinerary(itinerary);
    setPaymentAmount('');
    setShowPaymentModal(true);
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    try {
      const amount = parseFloat(paymentAmount);
      await independentItinerariesService.addPayment(selectedItinerary.id, amount);

      setShowPaymentModal(false);
      await loadData();
      alert('Payment recorded successfully!');
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Failed to record payment');
    }
  };

  const handleExport = () => {
    const csv = [
      ['Trip Name', 'Client', 'Destination', 'Start Date', 'End Date', 'Total Price', 'Amount Paid', 'Balance Due', 'Status'],
      ...filteredItineraries.map(i => [
        i.trip_name,
        getClientName(i.client_id),
        i.destination,
        i.start_date,
        i.end_date,
        i.total_price,
        i.amount_paid,
        i.balance_due,
        i.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `itineraries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isUpcoming = (itinerary) => {
    if (!itinerary.start_date) return false;
    const startDate = new Date(itinerary.start_date);
    const today = new Date();
    const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
    return daysUntil >= 0 && daysUntil <= 30;
  };

  // Filter and sort itineraries
  let filteredItineraries = itineraries.filter(itinerary => {
    const matchesSearch = itinerary.trip_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         itinerary.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getClientName(itinerary.client_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || itinerary.status === statusFilter;
    const matchesDate = (!dateFilter.start || itinerary.start_date >= dateFilter.start) &&
                       (!dateFilter.end || itinerary.start_date <= dateFilter.end);
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Sort itineraries
  filteredItineraries.sort((a, b) => {
    switch (sortBy) {
      case 'date_desc': return new Date(b.start_date) - new Date(a.start_date);
      case 'date_asc': return new Date(a.start_date) - new Date(b.start_date);
      case 'client': return getClientName(a.client_id).localeCompare(getClientName(b.client_id));
      case 'destination': return (a.destination || '').localeCompare(b.destination || '');
      default: return 0;
    }
  });

  const upcomingItineraries = filteredItineraries.filter(isUpcoming);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Itineraries</h1>
          <p className="text-gray-600 mt-1">Manage confirmed travel bookings</p>
        </div>
        <button
          onClick={() => navigate('/independent/quotes')}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          title="Itineraries are created by converting a sent quote"
        >
          <Plus size={20} className="mr-2" />
          Convert a Quote
        </button>
      </div>

      {/* Upcoming Trips Highlight */}
      {upcomingItineraries.length > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">
            🗓️ Upcoming Trips ({upcomingItineraries.length})
          </h3>
          <div className="space-y-2">
            {upcomingItineraries.slice(0, 3).map(itinerary => (
              <div key={itinerary.id} className="flex items-center justify-between text-sm">
                <span className="font-medium">{itinerary.trip_name}</span>
                <span className="text-blue-700">
                  {new Date(itinerary.start_date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search itineraries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="date_desc">Newest First</option>
            <option value="date_asc">Oldest First</option>
            <option value="client">Client Name</option>
            <option value="destination">Destination</option>
          </select>
          <button
            onClick={handleExport}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download size={18} className="mr-2" />
            Export
          </button>
        </div>

        {/* Date Range Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date From</label>
            <input
              type="date"
              value={dateFilter.start}
              onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date To</label>
            <input
              type="date"
              value={dateFilter.end}
              onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Itineraries Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItineraries.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">No itineraries found</p>
          </div>
        ) : (
          filteredItineraries.map((itinerary) => (
            <div key={itinerary.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Status Badge */}
                <div className="flex items-start justify-between mb-4">
                  {isUpcoming(itinerary) && (
                    <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full">
                      Upcoming
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(itinerary.status)}`}>
                    {itinerary.status}
                  </span>
                </div>

                {/* Itinerary Info */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{itinerary.trip_name}</h3>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600 flex items-center">
                    <MapPin size={16} className="mr-2" />
                    {itinerary.destination}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center">
                    <Calendar size={16} className="mr-2" />
                    {itinerary.start_date && new Date(itinerary.start_date).toLocaleDateString()} - {itinerary.end_date && new Date(itinerary.end_date).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600">👤 {getClientName(itinerary.client_id)}</p>
                </div>

                {/* Payment Info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-semibold">${parseFloat(itinerary.total_price || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Paid:</span>
                    <span className="font-semibold text-green-600">${parseFloat(itinerary.amount_paid || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Balance:</span>
                    <span className="font-semibold text-red-600">${parseFloat(itinerary.balance_due || 0).toLocaleString()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate(`/independent/itineraries/${itinerary.id}/edit`)}
                    className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => openPaymentModal(itinerary)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    title="Add Payment"
                  >
                    <DollarSign size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(itinerary.id)}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedItinerary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b px-6 py-4">
              <h2 className="text-2xl font-bold">Record Payment</h2>
              <p className="text-gray-600 mt-1">{selectedItinerary.trip_name}</p>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Current Balance: <span className="font-semibold text-red-600">${parseFloat(selectedItinerary.balance_due || 0).toLocaleString()}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPayment}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndependentItinerariesListEnhanced;
